import { gql } from "@apollo/client";
import { associatedRisk, documentDetail } from "../fragments";

export const ASSET_HIERARCHY_IMAGE = gql`
  query assetHierarchy($id: Int!) {
    assetHierarchy(id: $id) {
      id
      imageUrl
    }
  }
`;

export const LEASING_OFFER_INSURANCE_SIMULATION_IDS = gql`
  query leasingOffer($offerId: Int!, $offerSiteId: Int!) {
    leasingOffer(documentId: $offerId, siteId: $offerSiteId) @client {
      documentId
      siteId
      leasingSimulation {
        documentId
        siteId
        documentDetails {
          insuranceSimulations {
            id
          }
        }
      }
    }
  }
`;

export const LEASING_OFFER = gql`
  query leasingOffer($offerId: Int!, $offerSiteId: Int!) {
    leasingOffer(documentId: $offerId, siteId: $offerSiteId) {
      documentId
      siteId
      leasingSimulation {
        documentId
        siteId
        offerId
        offerSiteId
        allowInsuranceFinancing
        duration
        documentDetails {
          ...documentDetail
        }
        insuranceData {
          id
          siteId
          ownCasco
          ownRca
          ownRcaReadOnly
          ownCascoReadOnly
          financedInsuranceRca
          financedPeriodRca
          financedInsuranceCasco
          financedPeriodCasco
          financedInsuranceRti
          financedPeriodRti
        }
      }
    }
  }
  ${documentDetail}
`;

export const INSURANCE_SIMULATION_FOR_CASCO = gql`
  query insuranceSimulationForCasco($id: Int!, $offerId: Int!, $offerSiteId: Int!) {
    leasingSimulationByOffer(offerId: $offerId, offerSiteId: $offerSiteId) {
      documentId
      siteId
      duration
      contractTypeCode
      insured {
        id
        name
      }
    }
    insuranceSimulation(id: $id) {
      id
      insuranceTypeId
      insuranceTypeCode
      usageTypeCode
      financedInsurance
      financedInsurancePeriod
      categoryCode
      periodicity
      selectedInsuranceQuotationId
      ownerApplicationCode
      allowRtiOption(offerId: $offerId, offerSiteId: $offerSiteId)
      rtiEligible
      externalInsuranceDocumentId
      externalInsuranceDocumentNumber
      currentPolicyMaintained
      currencyId
      currencyCode
      insuredAmount
      hasRtiOption
      quotationsStatus
      policyDocumentNumber
      assetSafetyPackages {
        documentDetailId
        siteId
        code
      }
      selectedQuotation {
        id
        insurerId
        insurerName
        cascoQuotation {
          id
          insuranceAmount
          conventionInfo {
            conventionName
          }
        }
      }
    }
  }
`;

export const QUOTATIONS_FOR_CASCO = gql`
  query quotations($insuranceSimulationId: Int!) {
    quotations(insuranceSimulationId: $insuranceSimulationId) {
      id
      insuranceSimulationId
      insurerName
      freeTxt
      period
      sourceInsuranceQuotationId
      errorMessage
      blacklistInfo
      currencyCode
      cascoQuotation {
        id
        conventionId
        insuranceAmount
        totalInsuranceAmount
        totalDamageDeductiblePercent
        partialDamageDeductibleAmount
        periodicity
        insuredAmount
        quotationDate
        manualQuotation
        lockedYN
        conventionInfo {
          conventionName
          isActive
        }
        deductibleCosts {
          id
          insuranceQuotationId
          damageTypeCode
          deductibleCostValue
          currencyCode
        }
        associatedRisks {
          ...associatedRisk
        }
        coefficients {
          additionalDataTypeId
          additionalDataName
          allowMultipleDetails
          details {
            detailId
            detailName
            selected
          }
        }
      }
    }
  }
  ${associatedRisk}
`;

export const INSURANCE_SIMULATION_FOR_RCA = gql`
  query insuranceSimulation($id: Int!, $offerId: Int!, $offerSiteId: Int!) {
    leasingSimulationByOffer(offerId: $offerId, offerSiteId: $offerSiteId) {
      documentId
      siteId
      insured {
        id
        name
        drivingLicenseDate
        partnerTypeId
        partnerTypeCode
        defaultActivityFieldId
        defaultActivityFieldCode
        defaultActivityFieldName
        socialAddress {
          id
          districtId
          districtName
          cityId
          cityName
          street
          streetNumber
          zipCode
        }
      }
    }
    insuranceSimulation(id: $id) {
      id
      insuranceTypeId
      insuranceTypeCode
      insuranceTypeName
      usageTypeCode
      financedInsurance
      financedInsurancePeriod
      categoryCode
      subcategoryCode
      selectedInsuranceQuotationId
      ownerApplicationCode
      periodicity
      externalInsuranceDocumentId
      externalInsuranceDocumentNumber
      quotationsStatus
      policyDocumentNumber
      insuranceSimulationXPeriod {
        id
        insuranceSimulationId
        period
      }
    }
  }
`;

export const QUOTATIONS_FOR_RCA = gql`
  query quotations($insuranceSimulationId: Int!) {
    quotations(insuranceSimulationId: $insuranceSimulationId) {
      id
      insuranceSimulationId
      insurerName
      freeTxt
      period
      sourceInsuranceQuotationId
      errorMessage
      currencyCode
      rcaQuotation {
        id
        selectedDetailId
        details {
          id
          insuranceQuotationId
          directCompensationYN
          insuranceAmount
          commissionPercent
          commissionAmount
        }
      }
    }
  }
`;

export const PROPERTY_DETAILS_BY_CODE = gql`
  query propertyDetailsByCode($code: String!) {
    propertyDetailsByCode(code: $code) {
      id
      propertyId
      value
    }
  }
`;

export const CATEGORY_SUBCATEGORY_USAGE_TYPE_AND_ACTIVITY_FIELD = gql`
  query assetCategories($insuranceTypeCode: String!, $itemTypeCode: String!) {
    assetCategories(insuranceTypeCode: $insuranceTypeCode, itemTypeCode: $itemTypeCode) {
      id
      insuranceTypeCode
      code
      name
    }
    assetSubcategories {
      id
      code
      name
      categoryCode
    }
    usageTypes {
      id
      code
      name
    }
    activityFields {
      id
      code
      name
    }
  }
`;

export const LEASING_DOCUMENT_INSURANCE_DATA = gql`
  query leasingDocumentInsuranceDataByIds($offerId: Int!, $offerSiteId: Int!) {
    leasingDocumentInsuranceDataByIds(offerId: $offerId, offerSiteId: $offerSiteId) {
      id
      siteId
      ownCasco
      ownRca
      ownRcaReadOnly
      ownCascoReadOnly
      financedInsuranceRca
      financedPeriodRca
      financedInsuranceCasco
      financedPeriodCasco
      financedInsuranceRti
      financedPeriodRti
    }
    leasingSimulationByOffer(offerId: $offerId, offerSiteId: $offerSiteId) {
      documentId
      siteId
      allowInsuranceFinancing
      duration
    }
  }
`;

export const INSURANCE_SIMULATION_FOR_RTI = gql`
  query insuranceSimulationForRti($id: Int!, $offerId: Int!, $offerSiteId: Int!) {
    insuranceSimulation(id: $id) {
      id
      hasRtiOption
      policyDocumentNumber
      rtiOption {
        insuranceSimulationId
        insuranceAmount
        financedInsurance
        financedPeriod
        periodicity
        insurerName
        externalInsuranceDocumentId
        externalInsuranceDocumentNumber
      }
    }
    leasingSimulationByOffer(offerId: $offerId, offerSiteId: $offerSiteId) {
      documentId
      siteId
      duration
    }
  }
`;

export const USAGE_TYPE_AND_CATEGORY = gql`
  query usageTypesAndCategories($insuranceTypeCode: String!, $itemTypeCode: String!) {
    assetCategories(insuranceTypeCode: $insuranceTypeCode, itemTypeCode: $itemTypeCode) {
      id
      insuranceTypeCode
      code
      name
    }
    usageTypes {
      id
      code
      name
    }
    safetyPackages {
      id
      name
      code
      inactive
    }
  }
`;
